/* ============================================
   TREASURE BOX STUDIO — THEME JS
   ============================================ */

'use strict';

// ---- UTILITIES ----
const TBS = {
  formatMoney(amount) {
    if (amount == null) return '£0.00';
    const num = (parseInt(amount, 10) / 100).toFixed(2);
    return `£${num.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  },
  debounce(fn, delay = 300) {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
  },
  $: (sel, ctx = document) => ctx.querySelector(sel),
  $$: (sel, ctx = document) => [...ctx.querySelectorAll(sel)],
  on(el, event, fn) { if (el) el.addEventListener(event, fn); },
  off(el, event, fn) { if (el) el.removeEventListener(event, fn); },
};

// ---- ANNOUNCEMENT BAR ----
class AnnouncementBar {
  constructor() {
    this.bar = TBS.$('.announcement-bar');
    this.closeBtn = TBS.$('.announcement-bar__close');
    if (!this.bar) return;
    if (sessionStorage.getItem('ann-closed')) { this.bar.remove(); return; }
    TBS.on(this.closeBtn, 'click', () => {
      this.bar.style.maxHeight = this.bar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        this.bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease';
        this.bar.style.maxHeight = '0';
        this.bar.style.opacity = '0';
        this.bar.style.overflow = 'hidden';
      });
      setTimeout(() => this.bar.remove(), 350);
      sessionStorage.setItem('ann-closed', '1');
    });
  }
}

// ---- HEADER ----
class SiteHeader {
  constructor() {
    this.header = TBS.$('.site-header');
    this.searchToggle = TBS.$('[data-search-toggle]');
    this.searchPanel = TBS.$('.header-search');
    this.searchInput = TBS.$('.header-search__input');
    this.mobileToggle = TBS.$('.mobile-menu-toggle');
    this.mobileNav = TBS.$('.mobile-nav');
    this.mobileNavClose = TBS.$('.mobile-nav__close');
    this.mobileNavLinks = TBS.$$('.mobile-nav__link[data-has-sub]');
    this.overlay = TBS.$('#overlay');

    if (!this.header) return;
    this.init();
  }

  init() {
    // Sticky scroll effect
    window.addEventListener('scroll', TBS.debounce(() => {
      this.header.classList.toggle('scrolled', window.scrollY > 20);
    }, 50), { passive: true });

    // Search toggle
    TBS.on(this.searchToggle, 'click', () => this.toggleSearch());

    // Mobile menu
    TBS.on(this.mobileToggle, 'click', () => this.openMobileNav());
    TBS.on(this.mobileNavClose, 'click', () => this.closeMobileNav());

    // Mobile sub-menus
    this.mobileNavLinks.forEach(link => {
      TBS.on(link, 'click', (e) => {
        e.preventDefault();
        const sub = link.nextElementSibling;
        if (sub) sub.classList.toggle('active');
        const icon = TBS.$('svg', link);
        if (icon) icon.style.transform = sub?.classList.contains('active') ? 'rotate(180deg)' : '';
      });
    });

    // Overlay click closes drawers
    TBS.on(this.overlay, 'click', () => {
      this.closeMobileNav();
      this.closeSearch();
      CartDrawer.instance?.close();
      FilterDrawer.instance?.close();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMobileNav();
        this.closeSearch();
        CartDrawer.instance?.close();
        QuickView.instance?.close();
      }
    });
  }

  toggleSearch() {
    const isOpen = this.searchPanel?.classList.contains('active');
    if (isOpen) { this.closeSearch(); }
    else {
      this.searchPanel?.classList.add('active');
      this.overlay?.classList.add('active');
      setTimeout(() => this.searchInput?.focus(), 100);
    }
  }
  closeSearch() {
    this.searchPanel?.classList.remove('active');
    if (!this.mobileNav?.classList.contains('active') && !CartDrawer.instance?.isOpen()) {
      this.overlay?.classList.remove('active');
    }
  }
  openMobileNav() {
    this.mobileNav?.classList.add('active');
    this.mobileToggle?.classList.add('active');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  closeMobileNav() {
    this.mobileNav?.classList.remove('active');
    this.mobileToggle?.classList.remove('active');
    if (!CartDrawer.instance?.isOpen()) {
      this.overlay?.classList.remove('active');
      document.body.style.overflow = '';
    }
  }
}

// ---- CART DRAWER ----
class CartDrawer {
  static instance = null;

  constructor() {
    CartDrawer.instance = this;
    this.drawer = TBS.$('.cart-drawer');
    this.closeBtn = TBS.$('.cart-drawer__close');
    this.overlay = TBS.$('#overlay');
    this.itemsContainer = TBS.$('.cart-drawer__items');
    this.countEls = TBS.$$('.cart-count');
    this.subtotalEl = TBS.$('.cart-subtotal__value');
    this.shippingThreshold = window.TBS_CONFIG?.freeShippingThreshold || 5000;

    if (!this.drawer) return;
    TBS.on(this.closeBtn, 'click', () => this.close());

    // Open cart triggers
    TBS.$$('[data-cart-open]').forEach(btn => TBS.on(btn, 'click', () => this.open()));
    document.addEventListener('cart:open', () => this.open());
  }

  isOpen() { return this.drawer?.classList.contains('active'); }

  open() {
    this.refresh().then(() => {
      this.drawer?.classList.add('active');
      this.overlay?.classList.add('active');
      document.body.style.overflow = 'hidden';
    });
  }

  close() {
    this.drawer?.classList.remove('active');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  async refresh() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.error('Cart refresh failed', e);
    }
  }

  render(cart) {
    // Update count badges
    this.countEls.forEach(el => {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? 'flex' : 'none';
    });

    // Update drawer count
    const drawerCount = TBS.$('.cart-drawer__count');
    if (drawerCount) drawerCount.textContent = `(${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'})`;

    // Free shipping bar
    this.updateShippingBar(cart.total_price);

    if (cart.item_count === 0) {
      this.renderEmpty();
      return;
    }

    const html = cart.items.map(item => this.renderItem(item)).join('');
    if (this.itemsContainer) this.itemsContainer.innerHTML = html;

    // Subtotal
    if (this.subtotalEl) this.subtotalEl.textContent = TBS.formatMoney(cart.total_price);

    // Shipping label
    const shippingEl = TBS.$('.cart-subtotal__shipping');
    if (shippingEl) {
      if (cart.total_price >= this.shippingThreshold) {
        shippingEl.textContent = '✓ Free shipping applied';
        shippingEl.classList.add('free');
      } else {
        const remaining = this.shippingThreshold - cart.total_price;
        shippingEl.textContent = `Add ${TBS.formatMoney(remaining)} for free shipping`;
        shippingEl.classList.remove('free');
      }
    }

    // Bind qty/remove events
    this.bindItemEvents();
  }

  renderItem(item) {
    const personalisationProps = Object.entries(item.properties || {})
      .filter(([k]) => !k.startsWith('_'))
      .map(([k, v]) => v ? `<span class="cart-item__personalisation">✦ ${k}: ${v}</span>` : '')
      .join('');

    return `
      <div class="cart-item" data-key="${item.key}">
        <a class="cart-item__img" href="${item.url}">
          <img src="${item.image ? item.image.replace('_1024x1024', '_200x200') : ''}" alt="${item.title}" loading="lazy">
        </a>
        <div class="cart-item__details">
          <a class="cart-item__title" href="${item.url}">${item.product_title}</a>
          ${item.variant_title ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
          ${personalisationProps}
          <div class="cart-item__footer">
            <div class="cart-item__qty">
              <button class="cart-item__qty-btn" data-action="minus" aria-label="Decrease">−</button>
              <span class="cart-item__qty-val">${item.quantity}</span>
              <button class="cart-item__qty-btn" data-action="plus" aria-label="Increase">+</button>
            </div>
            <span class="cart-item__price">${TBS.formatMoney(item.final_line_price)}</span>
          </div>
          <button class="cart-item__remove" data-remove>Remove</button>
        </div>
      </div>`;
  }

  renderEmpty() {
    if (this.itemsContainer) {
      this.itemsContainer.innerHTML = `
        <div class="cart-drawer__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
          </svg>
          <p>Your cart is empty</p>
          <a href="/collections/all" class="btn btn-secondary btn-sm">Start Shopping</a>
        </div>`;
    }
    if (this.subtotalEl) this.subtotalEl.textContent = TBS.formatMoney(0);
  }

  updateShippingBar(totalPrice) {
    const fills = TBS.$$('.free-shipping-bar__fill');
    const texts = TBS.$$('.free-shipping-bar__text');
    const bars  = TBS.$$('.free-shipping-bar');
    if (!fills.length && !texts.length) return;

    const pct = Math.min((totalPrice / this.shippingThreshold) * 100, 100);
    const achieved = totalPrice >= this.shippingThreshold;
    const remaining = TBS.formatMoney(this.shippingThreshold - totalPrice);

    fills.forEach(fill => { fill.style.width = pct + '%'; });
    texts.forEach(text => {
      text.innerHTML = achieved
        ? '<strong>🎉 You\'ve unlocked free shipping!</strong>'
        : `Spend <strong>${remaining}</strong> more for free shipping`;
    });
    bars.forEach(bar => bar.classList.toggle('free-shipping-bar--achieved', achieved));
  }

  bindItemEvents() {
    TBS.$$('.cart-item__qty-btn', this.itemsContainer).forEach(btn => {
      TBS.on(btn, 'click', async (e) => {
        const item = btn.closest('.cart-item');
        const key = item?.dataset.key;
        const qtyEl = TBS.$('.cart-item__qty-val', item);
        if (!key || !qtyEl) return;

        const current = parseInt(qtyEl.textContent);
        const delta = btn.dataset.action === 'plus' ? 1 : -1;
        const newQty = Math.max(0, current + delta);
        await this.updateItem(key, newQty);
      });
    });

    TBS.$$('[data-remove]', this.itemsContainer).forEach(btn => {
      TBS.on(btn, 'click', async () => {
        const item = btn.closest('.cart-item');
        const key = item?.dataset.key;
        if (!key) return;
        item.style.opacity = '0.5';
        await this.updateItem(key, 0);
      });
    });
  }

  async updateItem(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await res.json();
      this.render(cart);
    } catch (e) {
      console.error('Cart update failed', e);
    }
  }

  static async addItem(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.description || 'Could not add to cart');
    }
    return res.json();
  }
}

// ---- QUICK VIEW ----
class QuickView {
  static instance = null;

  constructor() {
    QuickView.instance = this;
    this.modal = TBS.$('.quick-view');
    this.closeBtn = TBS.$('.quick-view__close');
    if (!this.modal) return;

    TBS.on(this.closeBtn, 'click', () => this.close());

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-view]');
      if (btn) this.open(btn.dataset.quickView, btn.dataset.productUrl);
    });
  }

  async open(handle, url) {
    this.modal.innerHTML = `<div class="quick-view__loading" style="grid-column:1/-1;display:flex;align-items:center;justify-content:center;min-height:300px"><div class="btn-loading" style="position:relative;width:40px;height:40px"><div style="width:32px;height:32px;border:2px solid var(--color-border);border-top-color:var(--color-gold);border-radius:50%;animation:spin 0.7s linear infinite;"></div></div></div>`;
    this.modal.classList.add('active');
    TBS.$('#overlay')?.classList.add('active');
    document.body.style.overflow = 'hidden';

    try {
      const res = await fetch(`${url}?view=quick-view`);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = TBS.$('.quick-view-content', doc);
      if (content) this.modal.innerHTML = content.innerHTML;
      else {
        // fallback: render basic product info
        this.modal.innerHTML = `<div style="grid-column:1/-1;padding:2rem;text-align:center"><p>View full product for details</p><a href="${url}" class="btn btn-primary" style="margin-top:1rem">View Product</a></div>`;
      }
      this.bindEvents();
    } catch (e) {
      this.modal.innerHTML = `<div style="grid-column:1/-1;padding:2rem;text-align:center"><p>Unable to load preview</p></div>`;
    }
  }

  close() {
    this.modal?.classList.remove('active');
    TBS.$('#overlay')?.classList.remove('active');
    document.body.style.overflow = '';
  }

  bindEvents() {
    TBS.on(TBS.$('.quick-view__close', this.modal), 'click', () => this.close());
  }
}

// ---- PRODUCT PAGE ----
class ProductPage {
  constructor() {
    this.form = TBS.$('[data-product-form]');
    if (!this.form) return;

    this.productData = JSON.parse(TBS.$('[data-product-json]')?.textContent || 'null');
    if (!this.productData) return;

    this.mainImg = TBS.$('.product-gallery__main img');
    this.thumbs = TBS.$$('.product-gallery__thumb');
    this.atcBtn = TBS.$('.add-to-cart-btn');
    this.stickyAtcBtn = TBS.$('.sticky-atc__btn');
    this.variantIdInput = TBS.$('[name="id"]', this.form);
    this.priceEl = TBS.$('[data-product-price]');
    this.comparePriceEl = TBS.$('[data-product-compare-price]');
    this.stockEl = TBS.$('[data-stock-indicator]');
    this.stickyAtc = TBS.$('.sticky-atc');
    this.atcSection = TBS.$('.product-atc');
    this.charCounters = TBS.$$('[data-char-limit]');

    this.currentVariant = this.productData.variants[0];

    this.initGallery();
    this.initVariants();
    this.initATC();
    this.initStickyATC();
    this.initCharCounters();
    this.initAccordions();
    this.initUrgency();
  }

  initGallery() {
    this.thumbs.forEach((thumb, i) => {
      TBS.on(thumb, 'click', () => {
        const src = thumb.dataset.src;
        const srcset = thumb.dataset.srcset;
        if (this.mainImg && src) {
          this.mainImg.src = src;
          if (srcset) this.mainImg.srcset = srcset;
        }
        this.thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  initVariants() {
    TBS.$$('[data-variant-select]').forEach(sel => {
      sel.addEventListener('change', () => this.updateVariant());
    });
    this.updateVariant();
  }

  updateVariant() {
    const selects = TBS.$$('[data-variant-select]');
    if (!selects.length) return;
    const variant = this.productData.variants.find(v =>
      selects.every(sel => String(v[sel.name] ?? '').trim() === String(sel.value).trim())
    );

    if (!variant) return;
    this.currentVariant = variant;

    // Update URL with variant param (no page reload)
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({ variantId: variant.id }, '', url.toString());

    // Update hidden id input
    if (this.variantIdInput) this.variantIdInput.value = variant.id;

    // Update price
    if (this.priceEl) this.priceEl.textContent = TBS.formatMoney(variant.price);
    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.textContent = TBS.formatMoney(variant.compare_at_price);
        this.comparePriceEl.style.display = '';
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    // Update ATC state
    const available = variant.available;
    if (this.atcBtn) {
      this.atcBtn.disabled = !available;
      const btnText = this.atcBtn.querySelector('.btn-text');
      if (btnText) btnText.textContent = available ? 'Add to Cart' : 'Sold Out';
    }
    if (this.stickyAtcBtn) {
      this.stickyAtcBtn.disabled = !available;
      this.stickyAtcBtn.textContent = available ? 'Add to Cart' : 'Sold Out';
    }
    const stickyPrice = TBS.$('.sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = TBS.formatMoney(variant.price);

    // Update gallery if variant has image
    if (variant.featured_image) {
      const matchThumb = TBS.$(`[data-src*="${variant.featured_image.src.split('?')[0].split('/').pop().split('_')[0]}"]`);
      if (matchThumb) matchThumb.click();
    }
  }

  initATC() {
    TBS.on(this.form, 'submit', async (e) => {
      e.preventDefault();
      if (!this.currentVariant?.available) return;

      const btn = this.atcBtn;
      const formData = new FormData(this.form);
      const properties = {};
      formData.forEach((val, key) => {
        if (key.startsWith('properties[') && key.endsWith(']')) {
          const propName = key.slice(11, -1);
          properties[propName] = val;
        }
      });

      btn?.classList.add('btn-loading');
      try {
        await CartDrawer.addItem(this.currentVariant.id, parseInt(formData.get('quantity') || 1), properties);
        CartDrawer.instance?.open();
        Toast.show('Added to your cart!', 'success');
        document.dispatchEvent(new CustomEvent('cart:item-added'));
      } catch (err) {
        Toast.show(err.message, 'error');
      } finally {
        btn?.classList.remove('btn-loading');
      }
    });
  }

  initStickyATC() {
    if (!this.stickyAtc || !this.atcSection) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        this.stickyAtc.classList.toggle('visible', !e.isIntersecting);
      });
    }, { threshold: 0.1 });
    observer.observe(this.atcSection);

    TBS.on(this.stickyAtcBtn, 'click', () => {
      this.atcSection.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => this.atcBtn?.click(), 600);
    });
  }

  initCharCounters() {
    this.charCounters.forEach(field => {
      const limit = parseInt(field.dataset.charLimit);
      const counter = field.parentElement.querySelector('.personalisation-field__char-count');
      TBS.on(field, 'input', () => {
        const remaining = limit - field.value.length;
        if (counter) counter.textContent = `${field.value.length}/${limit}`;
        if (field.value.length > limit) field.value = field.value.slice(0, limit);
      });
    });
  }

  initAccordions() {
    TBS.$$('.accordion-trigger').forEach(trigger => {
      TBS.on(trigger, 'click', () => {
        const item = trigger.closest('.accordion-item');
        const isOpen = item?.classList.contains('active');
        // Close all
        TBS.$$('.accordion-item.active').forEach(i => i.classList.remove('active'));
        // Open clicked (if it was closed)
        if (!isOpen) item?.classList.add('active');
      });
    });
  }

  initUrgency() {
    // Random "X people viewing" text
    const urgencyEl = TBS.$('[data-urgency-viewers]');
    if (urgencyEl) {
      const count = Math.floor(Math.random() * 8) + 3;
      urgencyEl.textContent = count;
    }

    // Low stock indicator
    const stockEl = TBS.$('[data-stock-count]');
    if (stockEl && this.currentVariant) {
      // Shopify doesn't expose inventory count in JS by default
      // This would be set via metafields or a custom approach
    }
  }
}

// ---- COLLECTION / FILTER DRAWER ----
class FilterDrawer {
  static instance = null;

  constructor() {
    FilterDrawer.instance = this;
    this.drawer = TBS.$('.filter-drawer');
    this.openBtn = TBS.$('[data-filter-open]');
    this.closeBtn = TBS.$('.filter-drawer__close');
    this.clearBtn = TBS.$('[data-filter-clear]');
    this.applyBtn = TBS.$('[data-filter-apply]');
    this.filterForm = TBS.$('[data-filter-form]');
    this.overlay = TBS.$('#overlay');

    if (!this.drawer) return;
    TBS.on(this.openBtn, 'click', () => this.open());
    TBS.on(this.closeBtn, 'click', () => this.close());
    TBS.on(this.applyBtn, 'click', () => { this.filterForm?.submit(); this.close(); });
    TBS.on(this.clearBtn, 'click', () => {
      TBS.$$('input[type="checkbox"]', this.filterForm).forEach(cb => cb.checked = false);
      this.filterForm?.submit();
    });
    this.initSortSelect();
    this.initViewToggle();
    this.initFilterTags();
  }

  open() {
    this.drawer?.classList.add('active');
    this.overlay?.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  close() {
    this.drawer?.classList.remove('active');
    this.overlay?.classList.remove('active');
    document.body.style.overflow = '';
  }

  initSortSelect() {
    const sortSel = TBS.$('[data-sort-select]');
    TBS.on(sortSel, 'change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSel.value);
      window.location.href = url.toString();
    });
  }

  initViewToggle() {
    TBS.$$('[data-view]').forEach(btn => {
      TBS.on(btn, 'click', () => {
        const view = btn.dataset.view;
        TBS.$$('[data-view]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const grid = TBS.$('.product-grid');
        if (grid) {
          grid.classList.toggle('product-grid--3', view === 'grid-3');
          grid.classList.toggle('product-grid', view === 'grid-4');
        }
        localStorage.setItem('pref-view', view);
      });
    });
    // Restore view preference
    const saved = localStorage.getItem('pref-view');
    if (saved) TBS.$(`[data-view="${saved}"]`)?.click();
  }

  initFilterTags() {
    TBS.$$('[data-filter-remove]').forEach(tag => {
      TBS.on(tag, 'click', () => {
        const key = tag.dataset.filterRemove;
        const url = new URL(window.location.href);
        url.searchParams.delete(key);
        window.location.href = url.toString();
      });
    });
  }
}

// ---- TESTIMONIALS SLIDER ----
class TestimonialsSlider {
  constructor() {
    this.track = TBS.$('.testimonials-track');
    if (!this.track) return;

    this.cards = TBS.$$('.testimonial-card', this.track);
    this.dots = TBS.$$('.slider-dot');
    this.prevBtn = TBS.$('[data-slider-prev]');
    this.nextBtn = TBS.$('[data-slider-next]');
    this.current = 0;
    this.perView = this.getPerView();
    this.total = Math.ceil(this.cards.length / this.perView);

    TBS.on(this.prevBtn, 'click', () => this.go(this.current - 1));
    TBS.on(this.nextBtn, 'click', () => this.go(this.current + 1));
    this.dots.forEach((dot, i) => TBS.on(dot, 'click', () => this.go(i)));

    window.addEventListener('resize', TBS.debounce(() => {
      this.perView = this.getPerView();
      this.total = Math.ceil(this.cards.length / this.perView);
      this.go(0);
    }));

    // Auto-advance
    this.autoTimer = setInterval(() => this.go(this.current + 1), 5000);
    this.track.addEventListener('mouseenter', () => clearInterval(this.autoTimer));
    this.track.addEventListener('mouseleave', () => {
      this.autoTimer = setInterval(() => this.go(this.current + 1), 5000);
    });

    // Touch support
    this.initTouch();
  }

  getPerView() {
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 3;
  }

  go(index) {
    this.current = ((index % this.total) + this.total) % this.total;
    const cardWidth = this.cards[0]?.offsetWidth || 0;
    const gap = 24;
    this.track.style.transform = `translateX(-${this.current * (cardWidth + gap) * this.perView}px)`;
    this.dots.forEach((dot, i) => dot.classList.toggle('active', i === this.current));
  }

  initTouch() {
    let startX = 0;
    this.track.addEventListener('touchstart', e => { startX = e.touches[0].clientX; }, { passive: true });
    this.track.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) this.go(this.current + (diff > 0 ? 1 : -1));
    });
  }
}

// ---- SCROLL REVEAL ----
class ScrollReveal {
  constructor() {
    if (!('IntersectionObserver' in window)) {
      TBS.$$('.reveal, .reveal--stagger, .reveal--zoom, .reveal--left, .reveal--right').forEach(el => el.classList.add('visible'));
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    TBS.$$('.reveal, .reveal--stagger, .reveal--zoom, .reveal--left, .reveal--right').forEach(el => obs.observe(el));
  }
}

// ---- SCROLL TO TOP ----
class ScrollTop {
  constructor() {
    this.btn = TBS.$('#scrollTopBtn');
    if (!this.btn) return;
    window.addEventListener('scroll', TBS.debounce(() => {
      this.btn.classList.toggle('visible', window.scrollY > 400);
    }, 100), { passive: true });
    TBS.on(this.btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }
}

// ---- TOAST NOTIFICATIONS ----
class Toast {
  static show(message, type = 'success', duration = 3500) {
    const existing = TBS.$('.toast');
    if (existing) existing.remove();

    const icons = {
      success: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>`,
      error: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>`,
    };

    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.innerHTML = `<span class="toast-icon">${icons[type] || icons.success}</span><span>${message}</span>`;
    document.body.appendChild(el);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => el.classList.add('show'));
    });

    setTimeout(() => {
      el.classList.remove('show');
      setTimeout(() => el.remove(), 400);
    }, duration);
  }
}

// ---- NEWSLETTER POPUP ----
class NewsletterPopup {
  constructor() {
    this.popup = TBS.$('.newsletter-popup');
    if (!this.popup) return;
    if (localStorage.getItem('nl-dismissed')) return;

    this.closeBtn = TBS.$('.newsletter-popup__close', this.popup);
    this.bg = TBS.$('.newsletter-popup__bg', this.popup);
    this.form = TBS.$('form', this.popup);

    setTimeout(() => this.popup.classList.add('active'), 4000);

    TBS.on(this.closeBtn, 'click', () => this.close());
    TBS.on(this.bg, 'click', () => this.close());
    TBS.on(this.form, 'submit', (e) => {
      e.preventDefault();
      this.close();
      Toast.show('Thanks! Check your inbox for your discount code.', 'success');
    });
  }
  close() {
    this.popup?.classList.remove('active');
    localStorage.setItem('nl-dismissed', '1');
  }
}

// ---- SOCIAL PROOF POPUP ----
class SocialProofPopup {
  constructor() {
    this.popup = TBS.$('.social-proof-popup');
    if (!this.popup) return;

    const proofs = [
      { name: 'Sarah M.', location: 'London', product: 'Personalised Baby Hamper', time: '2 minutes ago' },
      { name: 'Emma T.', location: 'Manchester', product: 'Wedding Signage Set', time: '5 minutes ago' },
      { name: 'Jessica W.', location: 'Bristol', product: 'Nursery Name Sign', time: '8 minutes ago' },
      { name: 'Charlotte H.', location: 'Edinburgh', product: 'Custom Birthday Plaque', time: '12 minutes ago' },
    ];

    let idx = 0;
    const show = () => {
      const proof = proofs[idx % proofs.length];
      TBS.$('.social-proof-popup__name', this.popup).textContent = proof.name;
      TBS.$('.social-proof-popup__action', this.popup).textContent = `just purchased: ${proof.product}`;
      TBS.$('.social-proof-popup__time', this.popup).textContent = proof.time;
      this.popup.classList.add('show');
      setTimeout(() => this.popup.classList.remove('show'), 4000);
      idx++;
    };

    TBS.on(TBS.$('.social-proof-popup__close', this.popup), 'click', () => {
      this.popup.classList.remove('show');
      clearInterval(this.timer);
    });

    setTimeout(() => {
      show();
      this.timer = setInterval(show, 12000);
    }, 6000);
  }
}

// ---- COUNTDOWN TIMER ----
class CountdownTimer {
  constructor() {
    TBS.$$('[data-countdown]').forEach(el => {
      const end = new Date(el.dataset.countdown);
      const update = () => {
        const diff = end - new Date();
        if (diff <= 0) { el.innerHTML = '<span class="countdown__label">Offer expired</span>'; return; }
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);

        const parts = d > 0
          ? [['days', d], ['hrs', h], ['min', m], ['sec', s]]
          : [['hrs', h], ['min', m], ['sec', s]];

        el.innerHTML = parts.map(([label, val], i) =>
          `${i > 0 ? '<span class="countdown__sep">:</span>' : ''}
           <div class="countdown__unit">
             <span class="countdown__num">${String(val).padStart(2, '0')}</span>
             <span class="countdown__label">${label}</span>
           </div>`
        ).join('');
      };
      update();
      setInterval(update, 1000);
    });
  }
}

// ---- PRODUCT CARD QUICK ADD (collection page) ----
class QuickAdd {
  constructor() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();

      const variantId = btn.dataset.quickAdd;
      if (!variantId) return;

      btn.disabled = true;
      const origText = btn.textContent;
      btn.textContent = '...';

      try {
        await CartDrawer.addItem(parseInt(variantId), 1);
        CartDrawer.instance?.open();
        Toast.show('Added to cart!', 'success');
        btn.textContent = '✓ Added';
        setTimeout(() => { btn.textContent = origText; btn.disabled = false; }, 2000);
      } catch (err) {
        Toast.show(err.message || 'Could not add to cart', 'error');
        btn.textContent = origText;
        btn.disabled = false;
      }
    });
  }
}

// ---- WISHLIST (localStorage) ----
class Wishlist {
  constructor() {
    // Migrate legacy flat-ID array to object map if needed
    const raw = JSON.parse(localStorage.getItem('tbs-wishlist') || '{}');
    this.items = Array.isArray(raw)
      ? Object.fromEntries(raw.map(id => [id, { id, handle: '', title: '', price: 0, image: '', url: '' }]))
      : raw;
    this.save();
    this.updateAll();

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist]');
      if (!btn) return;
      const id = btn.dataset.wishlist;
      const added = this.toggle(id, btn);
      btn.classList.toggle('active', added);
      Toast.show(added ? 'Added to wishlist ♥' : 'Removed from wishlist', added ? 'success' : 'info');
    });
  }

  toggle(id, btn) {
    if (this.items[id]) {
      delete this.items[id];
      this.save();
      return false;
    }
    this.items[id] = {
      id,
      handle: btn?.dataset.wishlistHandle || '',
      title:  btn?.dataset.wishlistTitle  || '',
      price:  parseInt(btn?.dataset.wishlistPrice  || '0', 10),
      image:  btn?.dataset.wishlistImage  || '',
      url:    btn?.dataset.wishlistUrl    || ''
    };
    this.save();
    return true;
  }

  save() {
    localStorage.setItem('tbs-wishlist', JSON.stringify(this.items));
  }

  ids() { return Object.keys(this.items); }

  updateAll() {
    TBS.$$('[data-wishlist]').forEach(btn => {
      btn.classList.toggle('active', !!this.items[btn.dataset.wishlist]);
    });
  }
}

// ---- PRODUCT GALLERY ZOOM + MOBILE SWIPE ----
class GalleryZoom {
  constructor() {
    const mainMedia = TBS.$('.product-gallery__main');
    if (!mainMedia) return;

    // Desktop: zoom on hover
    if (window.innerWidth > 768) {
      mainMedia.addEventListener('mousemove', (e) => {
        const img = TBS.$('#mainProductImg');
        if (!img) return;
        const rect = mainMedia.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = 'scale(1.7)';
      });
      mainMedia.addEventListener('mouseleave', () => {
        const img = TBS.$('#mainProductImg');
        if (img) img.style.transform = '';
      });
    }

    // Mobile: swipe between images
    this.initMobileSwipe(mainMedia);
  }

  initMobileSwipe(mainMedia) {
    const thumbs = TBS.$$('.product-gallery__thumb');
    const dots = TBS.$$('.product-gallery__dot');
    if (thumbs.length < 2) return;

    let startX = 0;
    let currentIndex = 0;

    const updateDots = (index) => {
      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === index);
      });
    };

    // Also sync dots when thumbs are clicked
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        currentIndex = i;
        updateDots(i);
      });
    });

    mainMedia.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    mainMedia.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 40) return;

      if (diff > 0) {
        currentIndex = Math.min(currentIndex + 1, thumbs.length - 1);
      } else {
        currentIndex = Math.max(currentIndex - 1, 0);
      }
      thumbs[currentIndex]?.click();
      updateDots(currentIndex);

      // Scroll thumbnail into view
      thumbs[currentIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    });
  }
}

// ---- PROMO TABS (product grid section) ----
class PromoTabs {
  constructor() {
    TBS.$$('.tab-btn').forEach(btn => {
      TBS.on(btn, 'click', () => {
        const section = btn.closest('[data-tabs-section]');
        if (!section) return;
        TBS.$$('.tab-btn', section).forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const target = btn.dataset.tab;
        TBS.$$('[data-tab-panel]', section).forEach(panel => {
          panel.style.display = panel.dataset.tabPanel === target ? 'grid' : 'none';
        });
      });
    });
  }
}

// ---- SEARCH SUGGESTIONS ----
class SearchSuggestions {
  constructor() {
    const input = TBS.$('.header-search__input');
    if (!input) return;

    const suggestionsEl = TBS.$('.header-search__suggestions');
    if (!suggestionsEl) return;

    const search = TBS.debounce(async (q) => {
      if (q.length < 2) { suggestionsEl.innerHTML = ''; return; }
      try {
        const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=5`);
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        
        if (products.length > 0) {
          suggestionsEl.innerHTML = products.map(p =>
            `<a href="${p.url}" class="search-suggestion">
              <div class="search-suggestion__image">
                <img src="${p.image}" alt="" loading="lazy">
              </div>
              <div class="search-suggestion__info">
                <span class="search-suggestion__title">${p.title}</span>
                ${p.price ? `<span class="search-suggestion__price">${p.price}</span>` : ''}
              </div>
            </a>`
          ).join('') + `<a href="/search?q=${encodeURIComponent(q)}&type=product" class="search-suggestion search-suggestion--view-all">View all results &rarr;</a>`;
        } else {
          suggestionsEl.innerHTML = `<div class="search-suggestion search-suggestion--empty">No products found for "${q}"</div>`;
        }
      } catch {}
    }, 300);

    input.addEventListener('input', () => search(input.value));
  }
}

// ---- CART PAGE QTY ----
class CartPageQty {
  constructor() {
    TBS.$$('.cart-qty-btn').forEach(btn => {
      TBS.on(btn, 'click', async () => {
        const row = btn.closest('[data-cart-key]');
        const key = row?.dataset.cartKey;
        const qtyEl = TBS.$('.cart-qty-val', row);
        if (!key || !qtyEl) return;
        const newQty = Math.max(0, parseInt(qtyEl.textContent) + (btn.dataset.action === 'plus' ? 1 : -1));
        row.style.opacity = '0.5';
        try {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty }),
          });
          window.location.reload();
        } catch {}
      });
    });
  }
}

// ---- ANNOUNCEMENT CAROUSEL ----
class AnnouncementCarousel {
  constructor() {
    this.slider = TBS.$('[data-announcement-slider]');
    if (!this.slider) return;

    this.slides = TBS.$$('.announcement-bar__slide', this.slider);
    if (this.slides.length < 2) return;

    this.current = 0;
    const speed = parseInt(this.slider.closest('[data-rotation-speed]')?.dataset?.rotationSpeed || '4') * 1000;

    this.timer = setInterval(() => this.next(), speed > 0 ? speed : 4000);
  }

  next() {
    const prev = this.slides[this.current];
    this.current = (this.current + 1) % this.slides.length;
    const next = this.slides[this.current];

    prev.classList.add('exit-up');
    prev.classList.remove('active');

    next.classList.add('active');
    next.classList.remove('exit-up');

    // Clean exit-up from prev after transition
    setTimeout(() => prev.classList.remove('exit-up'), 500);
  }
}

// ---- DISPATCH COUNTDOWN TIMER ----
class DispatchCountdown {
  constructor() {
    this.el = TBS.$('[data-dispatch-countdown]');
    if (!this.el) return;

    this.timeEl = TBS.$('[data-dispatch-time]', this.el);
    if (!this.timeEl) return;

    this.cutoffHour = parseInt(this.el.dataset.cutoffHour || '14');
    this.update();
    this.interval = setInterval(() => this.update(), 60000); // Update every minute
  }

  update() {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun, 6 = Sat

    // Weekend? Hide countdown
    if (day === 0 || day === 6) {
      this.el.querySelector('span').innerHTML =
        '<strong>Order now</strong> for dispatch on <strong>Monday</strong>';
      return;
    }

    const cutoff = new Date(now);
    cutoff.setHours(this.cutoffHour, 0, 0, 0);

    if (now >= cutoff) {
      // Past cutoff — show next business day
      const nextDay = day === 5 ? 'Monday' : 'tomorrow';
      this.el.querySelector('span').innerHTML =
        `<strong>Order now</strong> for dispatch <strong>${nextDay}</strong>`;
      return;
    }

    const diff = cutoff - now;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    parts.push(`${minutes}m`);

    this.timeEl.textContent = parts.join(' ');
  }
}

// ---- DELIVERY DATE ESTIMATOR ----
class DeliveryEstimator {
  constructor() {
    this.els = TBS.$$('[data-delivery-date]');
    if (this.els.length === 0) return;

    const estimate = this.calculate();
    this.els.forEach(el => { el.textContent = estimate; });
  }

  calculate() {
    const now = new Date();
    const min = this.addBusinessDays(now, 3);
    const max = this.addBusinessDays(now, 5);

    const opts = { month: 'short', day: 'numeric' };
    const minStr = min.toLocaleDateString('en-GB', opts);
    const maxStr = max.toLocaleDateString('en-GB', opts);

    return `${minStr} – ${maxStr}`;
  }

  addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const dow = result.getDay();
      if (dow !== 0 && dow !== 6) added++;
    }
    return result;
  }
}

// ---- ENHANCED ATC — SUCCESS ANIMATION ----
class ATCAnimation {
  constructor() {
    // Override the existing ATC form handler to add success animation
    const form = TBS.$('[data-product-form]');
    if (!form) return;

    // Listen for successful cart additions
    document.addEventListener('cart:item-added', () => {
      const btn = TBS.$('.add-to-cart-btn');
      if (!btn) return;

      const textEl = TBS.$('.btn-text', btn);
      const origText = textEl?.textContent;

      btn.classList.add('atc-success');
      if (textEl) textEl.textContent = '✓ Added to Cart';

      setTimeout(() => {
        btn.classList.remove('atc-success');
        if (textEl) textEl.textContent = origText;
      }, 2200);
    });
  }
}

// ---- INIT ----
document.addEventListener('DOMContentLoaded', () => {
  new AnnouncementBar();
  new SiteHeader();
  new CartDrawer();
  new QuickView();
  new ProductPage();
  new FilterDrawer();
  new TestimonialsSlider();
  new ScrollReveal();
  new ScrollTop();
  new NewsletterPopup();
  new SocialProofPopup();
  new CountdownTimer();
  new QuickAdd();
  new Wishlist();
  new GalleryZoom();
  new PromoTabs();
  new SearchSuggestions();
  new CartPageQty();

  // --- NEW: Professional upgrade modules ---
  new AnnouncementCarousel();
  new DispatchCountdown();
  new DeliveryEstimator();
  new ATCAnimation();
});
